import type {
    ContentLink,
    ContentInlineVideoProps,
    ContentVideoSource,
    ThemeItem,
    ThemeType,
    ContentImage,
    ContentBlock,
    BlockType,
    ContentResourceLocation,
    BreakpointType,
} from './content';
import {
    ContentBlockThemeStyleItems
} from './content';
import type {
    RoundedValueCustom
} from 'x1-base/types';
import type {
    FullImage
} from '~/types';



export type ContentBlockButtonTypeLink = 'link' | 'external' | 'youtube' | 'video' | 'image';

export interface ContentBlockCustomIcon {
    icon?: ThemeItem<ContentResourceLocation>;
}

export interface ContentBlockTextTitle extends ContentBlockCustomIcon {
    content?: Nullable<string>;
}

export interface ContentBlockTextSubTitle extends ContentBlockCustomIcon {
    content?: Nullable<string>;
}

export interface ContentBlockTextCaption extends ContentBlockCustomIcon {
    content?: Nullable<string>;
}

export interface ContentBlockTextBody extends ContentBlockCustomIcon {
    content?: Nullable<string>;
}

export interface ContentBlockTextButtonItem extends ContentLink {
    type: ContentBlockButtonTypeLink;
    content: Nullable<string>;
    theme: ThemeItem<ContentBlockThemeStyleItems>;
}

export interface ContentBlockTextButton {
    items: ContentBlockTextButtonItem[];
}

// Text Block
export interface ContentBlockText {
    title: ContentBlockTextTitle;
    subTitle?: ContentBlockTextSubTitle;
    caption?: ContentBlockTextCaption;
    body: ContentBlockTextBody;
    buttonsCssClass?: string[];
    buttons: ContentBlockTextButton;
    showUnderlay?: boolean;
}

// Image Block
export interface ContentBlockImageStaticConfigBase {
    altText: string;
    plainImage?: boolean;
    contain?: boolean;
    autoResponsive?: boolean;
    fluidWidth?: boolean;
    fluidHeight?: boolean;
    fillHeight?: boolean;
    fillWidth?: boolean;
    position?: string;
    rounded?: RoundedValueCustom;
}

// Video Block
export interface ContentBlockVideoStaticConfigBase extends ContentBlockImageStaticConfigBase {
    videoUrlDesktop: string;
    videoUrlMobile: string;
}

export interface ContentBlockImageStatic {
    base: Partial<ContentBlockImageStaticConfigBase>;
    breakpoint?: PartialRecord<BreakpointType, Partial<ContentBlockImageStaticConfigBase>>;
    theme?: ThemeItem<ContentImage>;
    component?: {
        breakpoint?: PartialRecord<BreakpointType, ContentBlockThemeStyleItems>;
    }
}

export interface ContentBlockVideoStatic {
    base: Partial<ContentBlockVideoStaticConfigBase>;
    breakpoint?: PartialRecord<BreakpointType, Partial<ContentBlockVideoStaticConfigBase>>;
    theme?: ThemeItem<ContentImage>;
    component?: {
        breakpoint?: PartialRecord<BreakpointType, ContentBlockThemeStyleItems>;
    }
}

// Inline Video Block
export interface ContentBlockInlineVideo extends ContentLink, ContentVideoSource {
    video: ContentInlineVideoProps;
}

// Blocks

export type ContentBlockType = 'text' | 'image-static' | 'video-static' | 'inline-video';

export type ContentBlockConfigType = ContentBlockText | ContentBlockImageStatic | ContentBlockVideoStatic | ContentBlockInlineVideo;

// Block Layout Type

export enum LayoutTypeForFeatures {
    HALF = 'half',
    VERTICAL = 'vertical',
    HORIZONTAL = 'horizontal',
    STACKED = 'stacked'
}


export interface ContentBlockCommonProps {
    theme: ThemeType;
    breakpoint: string;
    breakpointName: BreakpointType;
    isHyperlinkBlock?: boolean;
    isMobile: boolean;
}

export interface ContentBlockComponentProps extends ContentBlockCommonProps {
    block: ContentBlock;
}

export interface ContentSubBlockComponentProps extends ContentBlockCommonProps {
    id?: string;
    subBlock: BlockType;
}

export interface ContentBlocksComponentProps<T> extends ContentBlockCommonProps {
    config: T;
}


// - - - - - Content Blocks V2 - - - - -

export type ContentBlockRenderedHeight = 'auto' | 'small' | 'medium' | 'large';
export type ContentBlockRenderedHostedVideoService = 'youtube' | 'vimeo';

export type ContentBlockRenderedMediaSideX = 'left' | 'right';
export type ContentBlockRenderedMediaSideY = 'top' | 'bottom';
export type ContentBlockRenderedMediaSideZ = 'above' | 'below';

export type ContentBlockRenderedTextBlockTheme = 'light' | 'dark';



export interface ContentBlockRenderedLink {
    kind: 'external' | 'page' | 'product' | 'category';
    target: string;
    url: Nullable<string>;
}

export interface ContentBlockRenderedVideo {
    url: string;
    altText?: string;
}

export interface ContentBlockRenderedHostedVideo {
    service: ContentBlockRenderedHostedVideoService;
    id: string;
}


/**
 * text/markdown/choice slots → string; number slots → number;
 * video slots → file URL string; group slots → nested object;
 * list slots → array of the `of` slot's value type.
 */
export type ContentBlockRenderedValue =
    | string
    | number
    | boolean
    | FullImage
    | ContentBlockRenderedLink
    | ContentBlockRenderedValue[]
    | { [slotKey: string]: ContentBlockRenderedValue }
    | null;


export interface ContentBlockRenderedTemplate {
    ref: string;
    version: number;
}

export interface ContentBlockRenderedLinked {
    placement: string;
    provided: boolean;
}

export interface ContentBlockRenderedLinkedOption {
    placement: string;
    pk: number;
    label: string;
}

export interface ContentBlockRenderedLinkedOptionsResponse {
    options: ContentBlockRenderedLinkedOption[];
}

export interface ContentBlockRendered<TValues = Record<string, ContentBlockRenderedValue>> {
    schema: string;
    schemaVersion: number;
    template: ContentBlockRenderedTemplate | null;
    placements: string[];
    // Present only for schemas with mapped slots.
    linked?: ContentBlockRenderedLinked;
    values: TValues;
    ref?: string;
}

export interface ContentBlockRenderedListItem<TValues = Record<string, ContentBlockRenderedValue>>
    extends ContentBlockRendered<TValues> {
    uid: string;
}

export interface ContentBlocksByPlacementResponse<TItem = ContentBlockRenderedListItem> {
    blocks: TItem[];
}

export type ContentBlockRenderedPosition =
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'middle-left'
    | 'middle-center'
    | 'middle-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';

export type ContentBlockRenderedTextAlign =
    | 'left'
    | 'center'
    | 'right'
    | 'justify';

export type ContentBlockRenderedWidthType =
    | 'fluid'
    | 'constrained';

export type ContentBlockRenderedImagePosition =
    | 'left'
    | 'center'
    | 'right'
    | 'bottom'
    | 'unset';
